import React, { useMemo } from 'react';
import styled from 'styled-components';
import { getPriceChangedInfo } from '../../../../utils/getter/getPriceChangedInfo.util';
import { Button } from '../../../../components/common/buttons/Button';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { ampli } from '../../../../ampli';

interface CropPriceSummaryProps {
  avgPrice: number;
  maxPrice: number;
  minPrice: number;
  prevPrice: number;
  changeAmount: number;
  changeRate: number;
  totalKg: number;
  totalPrice: number;
  tradeCalculationDate: string;
  children?: React.ReactNode;
  categoryName: string;
}

export const CropPriceSummary = ({
  avgPrice,
  prevPrice,
  maxPrice,
  minPrice,
  changeAmount,
  changeRate,
  totalKg,
  totalPrice,
  tradeCalculationDate,
  children,
  categoryName,
}: CropPriceSummaryProps) => {
  const { openNotification } = useNotification();

  const month = new Date(tradeCalculationDate).getMonth() + 1;
  const day = new Date(tradeCalculationDate).getDate();

  const priceChangedInfo = getPriceChangedInfo(changeAmount);

  const handleShareButtonClick = () => {
    ampli.pvmClickCropDetailShareButton({ cultivar: categoryName });

    const pageUrlWithUtm = `${window.location.href}&utm_source=DM&utm_medium=sms&utm_campaign=agricultural_price_share&utm_term=250623`;

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'share', messageParam: pageUrlWithUtm });
    } else {
      window.navigator.clipboard.writeText(pageUrlWithUtm);
      openNotification({ message: '현재 게시물의 주소가 복사되었습니다.' });
    }
  };

  return (
    <>
      <AvgPriceAndToggleSection>
        <AvgPriceSection>
          <AvgPriceDescription>{'1kg당 평균'}</AvgPriceDescription>
          <AvgPriceInfoContainer>
            <AvgPrice>{avgPrice === 0 ? '당일 거래 없음' : `${avgPrice.toLocaleString()}원`}</AvgPrice>
            {avgPrice !== 0 && prevPrice !== 0 && (
              <DiffBeforePrice color={priceChangedInfo?.color ?? '#858894'}>
                {`${priceChangedInfo?.sign}${Math.abs(changeAmount).toLocaleString()}(${Math.abs(changeRate).toFixed(
                  1,
                )}%)`}
              </DiffBeforePrice>
            )}
          </AvgPriceInfoContainer>
        </AvgPriceSection>
      </AvgPriceAndToggleSection>
      {children}
      <ShareAndSummarySection>
        <Button
          borderType={'square'}
          color={'outlinedGreyLight'}
          size={'lg'}
          icon={{ fileName: 'Link', size: 'Large', fill: '#C6C6D5', position: 'left' }}
          text={{ size: 'lg', content: '시세 공유하기' }}
          onClick={() => handleShareButtonClick()}
        />
        <TotalDataSection>
          <TotalDataWrapper>
            <TotalDataTitle>{`${month}월 ${day}일 총 거래물량`}</TotalDataTitle>
            <TotalDataValue>{`${totalKg ? totalKg.toLocaleString() : '-'}kg`}</TotalDataValue>
          </TotalDataWrapper>
          <TotalDataWrapper>
            <TotalDataTitle>{`${month}월 ${day}일 총 거래금액`}</TotalDataTitle>
            <TotalDataValue>{`${totalPrice ? totalPrice.toLocaleString() : '-'}원`}</TotalDataValue>
          </TotalDataWrapper>
        </TotalDataSection>
        <PriceBoxContainer>
          <PriceBox>
            <PriceDescription>{'kg당 평균가'}</PriceDescription>
            <PriceValue color={'#232429'}>{`${avgPrice ? avgPrice.toLocaleString() : '-'}원`}</PriceValue>
          </PriceBox>
          <PriceBox>
            <PriceDescription>{'kg당 최고가'}</PriceDescription>
            <PriceValue color={'#FF3049'}>{`${maxPrice ? maxPrice.toLocaleString() : '-'}원`}</PriceValue>
          </PriceBox>
          <PriceBox>
            <PriceDescription>{'kg당 최저가'}</PriceDescription>
            <PriceValue color={'#196EFF'}>{`${minPrice ? minPrice.toLocaleString() : '-'}원`}</PriceValue>
          </PriceBox>
        </PriceBoxContainer>
      </ShareAndSummarySection>
    </>
  );
};

const AvgPriceAndToggleSection = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 16px;
  align-self: stretch;
  flex-wrap: wrap;

  padding: 0px 16px;
`;

const AvgPriceSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
`;

const AvgPriceDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const AvgPriceInfoContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 2px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const AvgPrice = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const DiffBeforePrice = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const ShareAndSummarySection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  padding: 0px 16px;
`;

const TotalDataSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const TotalDataWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const TotalDataTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */

  flex: 1 0 0;
`;

const TotalDataValue = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const PriceBoxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const PriceBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  flex: 1 0 0;

  padding: 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const PriceDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;

const PriceValue = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  text-align: center;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;
